import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { createPortal } from 'react-dom';
import { GenericForm, GenericFormField, GenericSelect } from '@/styles/GenericForm';
import { StandardButton, SecondaryButton } from '@/styles/StandardButtons';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import countries from '@/lib/countries';
import AddressAutocomplete, { AddressSelection } from '@/features/project/info/AddressAutocomplete';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { colors } from '@/design-system/colors';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { uploadTeamMemberAttachments, deleteTeamMemberAttachment, fetchTeamMemberAttachments, TeamMemberAttachment } from '../store';
import { useUser } from '@/contexts/UserContext';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  title: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  role: string;
  is_active: boolean;
  notes: string;
  created_at: string;
}

interface TeamFormProps {
  isOpen: boolean;
  teamMember: TeamMember | null;
  userRole: string;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  error?: string | null;
}

export function TeamForm({
  isOpen,
  teamMember,
  userRole,
  onClose,
  onSave,
  error: externalError,
}: TeamFormProps) {
  const { companyId, user } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const uploadTeamMemberAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadTeamMemberAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteTeamMemberAttachmentFn = useCallback((id: string) => {
    if (!user?.id) return Promise.resolve(false);
    return deleteTeamMemberAttachment(id, user.id);
  }, [user]);
  const {
    pendingFiles,
    uploadedFiles: attachments,
    setUploadedFiles: setAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending,
    handleDelete: handleFileDelete,
    resetPending: resetPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: teamMember?.id,
    uploadFn: uploadTeamMemberAttachmentsFn,
    deleteFn: deleteTeamMemberAttachmentFn,
    newFilesPosition: 'prepend',
    onError: setError,
  });
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  // Store original form data for unsaved changes detection
  const originalFormDataRef = useRef<Record<string, any>>({});

  // Compute initial values directly from teamMember prop
  // Key by teamMember.id to ensure GenericForm updates when switching between members
  const initialValues = useMemo(() => {
    if (!isOpen) return {};
    
    const teamMemberId = teamMember?.id || 'new';
    
    const values = teamMember ? {
      first_name: teamMember.first_name || '',
      last_name: teamMember.last_name || '',
      email: teamMember.email || '',
      phone: teamMember.phone || '',
      title: teamMember.title || '',
      address: teamMember.address || '',
      city: teamMember.city || '',
      state: teamMember.state || '',
      country: teamMember.country || '',
      zip_code: teamMember.zip_code || '',
      role: teamMember.role || 'member',
      is_active: teamMember.is_active !== undefined ? (teamMember.is_active ? 'active' : 'inactive') : 'active',
      notes: teamMember.notes || '',
      _teamMemberId: teamMemberId, // Include ID to force update when switching members
    } : {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      title: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      role: 'member',
      is_active: 'active',
      notes: '',
      _teamMemberId: teamMemberId,
    };
    
    // Initialize formData when form opens
    setFormData(values);
    // Store original form data for unsaved changes detection
    originalFormDataRef.current = JSON.parse(JSON.stringify(values));
    return values;
  }, [isOpen, teamMember?.id]);

  // Update error from external prop
  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  // Fetch attachments when team member changes
  useEffect(() => {
    if (isOpen && teamMember?.id && companyId) {
      setIsLoadingAttachments(true);
      fetchTeamMemberAttachments(teamMember.id, companyId)
        .then((fetchedAttachments) => {
          const genericAttachments: GenericAttachment[] = fetchedAttachments.map((att) => ({
            id: att.id,
            file_name: att.file_name,
            file_type: att.file_type,
            file_size: att.file_size,
            file_url: att.file_url,
            created_at: att.created_at,
            created_by: att.created_by,
            created_by_user: att.created_by_user,
          }));
          setAttachments(genericAttachments);
        })
        .catch((err) => {
          console.error('Error fetching attachments:', err);
          setAttachments([]);
        })
        .finally(() => {
          setIsLoadingAttachments(false);
        });
    } else {
      setAttachments([]);
      resetPendingFiles();
    }
  }, [isOpen, teamMember?.id, companyId, setAttachments, resetPendingFiles]);

  // Helper function to check if form data has changed
  const hasFormChanged = (current: Record<string, any>, original: Record<string, any>): boolean => {
    const allKeys = Array.from(new Set([...Object.keys(current), ...Object.keys(original)]));
    for (const key of allKeys) {
      // Skip internal fields
      if (key === '_teamMemberId') continue;
      
      const currentValue = current[key];
      const originalValue = original[key];
      const currentStr = currentValue == null ? '' : String(currentValue).trim();
      const originalStr = originalValue == null ? '' : String(originalValue).trim();
      if (currentStr !== originalStr) {
        return true;
      }
    }
    // Also check for pending files
    if (pendingFiles.length > 0) {
      return true;
    }
    return false;
  };

  const handleCloseAttempt = () => {
    const isDirty = hasFormChanged(formData, originalFormDataRef.current);
    if (isDirty) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onClose();
        // Reset form data to original
        setFormData(JSON.parse(JSON.stringify(originalFormDataRef.current)));
      }
    } else {
      onClose();
    }
  };

  const handleFieldChange = (name: string, value: any, updatedFormData: Record<string, any>) => {
    setFormData(updatedFormData);
  };

  // Helper function to match country from API to our countries list
  const matchCountry = (apiCountry: string | undefined): string | undefined => {
    if (!apiCountry) return undefined;
    
    // Try exact match first
    const exactMatch = countries.find(c => c.toLowerCase() === apiCountry.toLowerCase());
    if (exactMatch) return exactMatch;
    
    // Try common variations
    const countryVariations: Record<string, string> = {
      'us': 'United States',
      'usa': 'United States',
      'united states of america': 'United States',
      'uk': 'United Kingdom',
      'united kingdom of great britain and northern ireland': 'United Kingdom',
      'uae': 'United Arab Emirates',
      'south korea': 'South Korea',
      'north korea': 'North Korea',
      'russia': 'Russia',
      'russian federation': 'Russia',
    };
    
    const normalized = apiCountry.toLowerCase().trim();
    if (countryVariations[normalized]) {
      return countryVariations[normalized];
    }
    
    // Try partial match (e.g., "United States" matches "United States of America")
    const partialMatch = countries.find(c => 
      c.toLowerCase().includes(normalized) || normalized.includes(c.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    return undefined;
  };

  const handleAddressChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      address: value
    }));
  };

  const handleAddressSelect = (selection: AddressSelection) => {
    const matchedCountry = matchCountry(selection.country);
    setFormData(prev => ({
      ...prev,
      address: selection.addressLine1,
      city: selection.city || prev.city,
      state: selection.state || prev.state,
      zip_code: selection.postalCode || prev.zip_code,
      ...(matchedCountry && { country: matchedCountry }),
    }));
  };


  const handleSubmit = async (data: Record<string, any>) => {
    // Validate required fields
    if (!data.first_name?.trim() || !data.last_name?.trim() || !data.email?.trim()) {
      setError('First name, last name, and email are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(data.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert is_active back to boolean
      const submitData = {
        ...data,
        is_active: data.is_active === 'active',
      };

      // Save team member first
      await onSave(submitData);

      // If there are pending files and we're in create mode, we need to wait for the team member to be created
      // The parent component should handle this by calling onSave and then we can upload files
      // For now, we'll close and let the parent refresh, which will show the new member
      // and allow file uploads in edit mode
      
      onClose();
    } catch (err: any) {
      console.error('Error saving team member:', err);
      setError(err.message || 'Failed to save team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const teamFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'first_name',
      label: 'First Name',
      type: 'text',
      required: true,
      row: 1,
      colSpan: 1,
      placeholder: 'Enter first name',
    },
    {
      name: 'last_name',
      label: 'Last Name',
      type: 'text',
      required: true,
      row: 1,
      colSpan: 1,
      placeholder: 'Enter last name',
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      row: 2,
      colSpan: 1,
      placeholder: 'Enter email address',
    },
    {
      name: 'phone',
      label: 'Phone',
      type: 'text',
      row: 2,
      colSpan: 1,
      placeholder: 'Enter phone number',
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      row: 3,
      colSpan: 0.5,
      placeholder: 'Enter job title (e.g., CEO, PM, Estimator)',
    },
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      row: 3,
      colSpan: 0.5,
      options: [
        { label: 'Member', value: 'member' },
        ...(userRole === 'admin' ? [{ label: 'Admin', value: 'admin' }] : []),
      ],
      placeholder: 'Select',
      disabled: userRole !== 'admin',
    },
    {
      name: 'is_active',
      label: 'Status',
      type: 'select',
      row: 3,
      colSpan: 0.5,
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
      placeholder: 'Select',
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      row: 4,
      colSpan: 2,
      placeholder: 'Enter street address',
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter city',
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter state',
    },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      row: 5,
      colSpan: 0.5,
      options: countries.map(c => ({ label: c, value: c })),
      placeholder: 'Select',
    },
    {
      name: 'zip_code',
      label: 'ZIP Code',
      type: 'text',
      row: 5,
      colSpan: 0.5,
      placeholder: 'Enter ZIP code',
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      row: 7,
      colSpan: 2,
      placeholder: 'Enter any additional notes',
    },
  ], [userRole]);

  // Group fields by row
  const fieldsByRow = teamFields.reduce((acc, field) => {
    const row = field.row || 999;
    if (!acc[row]) acc[row] = [];
    acc[row].push(field);
    return acc;
  }, {} as Record<number, GenericFormField[]>);

  const sortedRows = Object.keys(fieldsByRow).sort((a, b) => Number(a) - Number(b));

  // Helper to render a field
  const renderField = (field: GenericFormField) => {
    const fieldValue = (formData as Record<string, any>)[field.name] || '';
    if (field.type === 'select') {
      return (
        <GenericSelect
          value={String(fieldValue)}
          onValueChange={(value) => handleFieldChange(field.name, value, { ...formData, [field.name]: value })}
          disabled={field.disabled}
          required={field.required}
          placeholder={field.placeholder}
          contentClassName={field.name === 'country' ? "max-h-[18.75rem] overflow-y-auto" : ""}
          options={field.options || []}
        />
      );
    } else if (field.type === 'textarea') {
      return (
        <Textarea
          id={field.name}
          value={String(fieldValue)}
          onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          disabled={field.disabled}
        />
      );
    } else {
      return (
        <Input
          id={field.name}
          type={field.type}
          value={String(fieldValue)}
          onChange={(e) => handleFieldChange(field.name, e.target.value, { ...formData, [field.name]: e.target.value })}
          placeholder={field.placeholder}
          className="font-form-input generic-form-field"
          disabled={field.disabled}
        />
      );
    }
  };

  const customLayout = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(formData);
      }}
      className="generic-form-container"
      style={{ maxHeight: 'var(--form-max-height-default)' }}
    >
      {/* Header */}
      <div 
        className={cn("generic-form-header", "generic-form-header-default")}
        style={{ backgroundColor: colors.formHeaderFooter }}
      >
        {/* People > Team is edit-only by design -- adding a team member is
            a Settings > Users-only flow (see TeamPage's handleSaveTeamMember).
            This form is only ever opened with a real teamMember. */}
        <h2 className="font-form-title">
          Edit Team Member
        </h2>
        <button
          type="button"
          onClick={handleCloseAttempt}
          className="generic-close-button"
        >
          <svg className="generic-close-button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className={cn("generic-form-body", "generic-form-body-default")}>
        {error && (
          <div className="generic-form-error">
            {error}
          </div>
        )}
        <div className="generic-form-body-spacing-default">
          {/* Row 1: First Name, Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[1]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 2: Email, Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[2]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 3: Title, Role, Status (1/4 width each, leaving 1/4 empty) */}
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[3]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 4: Address (full width) with AddressAutocomplete */}
          <div className="space-y-2">
            <Label htmlFor="address" className="font-form-label">
              Address
            </Label>
            <AddressAutocomplete
              value={formData.address || ''}
              onChange={handleAddressChange}
              onSelectSuggestion={handleAddressSelect}
              placeholder="Start typing address"
              inputClassName="w-full"
              initialSelected={Boolean(teamMember)}
            />
          </div>

          {/* Row 5: City, State, Country, ZIP Code (1/4 width each) */}
          <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 'var(--field-gap-default)' }}>
            {fieldsByRow[5]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Row 7: Notes */}
          <div className="space-y-2">
            {fieldsByRow[7]?.map((field) => (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="font-form-label">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Attachments Section */}
          <div className="space-y-2">
            <GenericAttachmentsSection
              attachments={attachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={teamMember?.id ? handleFileDelete : undefined}
              onRemovePending={handleRemovePending}
              projectId={null} mode={teamMember ? 'edit' : 'create'}
              disabled={isSubmitting || isLoadingAttachments}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div 
        className={cn("generic-form-footer", "generic-form-footer-default")}
        style={{ backgroundColor: colors.formHeaderFooter }}
      >
        <StandardButton
          customVariant="white"
          onClick={handleCloseAttempt}
        >
          Cancel
        </StandardButton>
        <SecondaryButton
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </SecondaryButton>
      </div>
    </form>
  );

  return (
    <GenericForm
      title="Team Member"
      mode="edit"
      fields={teamFields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCloseAttempt}
      isOpen={isOpen}
      error={error}
      submitButtonText="Save Changes"
      saving={isSubmitting}
      onFieldChange={handleFieldChange}
      customLayout={customLayout}
    />
  );
}

